import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCategories, useSubCategoriesByCategory } from "../../../hooks/store/categories";
import { categoriesOwnerHomeType } from "../../../types/enum/categoriesOwnerHome";

type Props = {
  cities: string[];
};

const SearchForm: React.FC<Props> = ({ cities }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    name: searchParams.get("name") || "",
    phone: searchParams.get("phone") || "",
    commercialRegister: searchParams.get("commercialRegister") || "",
    city: searchParams.get("city") || "",
    type: searchParams.get("type") || "",
    subType: searchParams.get("subType") || "",
    dateFilter: searchParams.get("dateFilter") || "" // new filter
  });

  const dateFilters =[
  { id: "year", name: t(categoriesOwnerHomeType.THISYEAR) },
  { id: "month", name: t(categoriesOwnerHomeType.THISMONTH) },
  { id: "week", name: t(categoriesOwnerHomeType.THISWEEK) },
];

  // Fetch categories
  const { data: categories = [] } = useCategories();
  // Fetch subCategories based on selected type
  const { data: subCategories = [] } = useSubCategoriesByCategory(+filters.type);

  // Update filters state
  const handleChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      ...(key === "type" ? { subType: "" } : {})
    }));
  };


  const handleSubmit = (e: React.FormEvent) => {
  e.preventDefault();

  // Start with current URL search params
  const newParams = new URLSearchParams(searchParams.toString());

  // Update with form filters
  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      newParams.set(key, value); // add or update filter
    } else {
      newParams.delete(key); // remove filter if empty
    }
  });

  // Reset page to 1 if needed
  if (newParams.toString()) newParams.set("page", "1");

  setSearchParams(newParams);
};


  // Clear param immediately if empty
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams.toString());
    Object.entries(filters).forEach(([key, value]) => {
      if (!value && newParams.has(key)) {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  }, [filters, searchParams, setSearchParams]);

  return (
    <form onSubmit={handleSubmit} className="flex flex-wrap gap-5">
      <input
        type="text"
        placeholder={t("search_by_name")}
        value={filters.name}
        onChange={e => handleChange("name", e.target.value)}
        className="border rounded-lg px-3 py-2 w-[265px] focus:outline-none focus:ring-2 focus:ring-primary"
      />
      <input
        type="text"
        placeholder={t("search_by_phone")}
        value={filters.phone}
        onChange={e => handleChange("phone", e.target.value)}
        className="border rounded-lg px-3 py-2 w-[265px] focus:outline-none focus:ring-2 focus:ring-primary"
      />
      <input
        type="text"
        placeholder={t("commercialRegister")}
        value={filters.commercialRegister}
        onChange={e => handleChange("commercialRegister", e.target.value)}
        className="border rounded-lg px-3 py-2 w-[265px] focus:outline-none focus:ring-2 focus:ring-primary"
      />
      <select
        value={filters.city}
        onChange={e => handleChange("city", e.target.value)}
        className="border rounded-lg px-3 py-2 w-[265px] focus:outline-none focus:ring-2 focus:ring-primary"
      >
        <option value="">{t("search_by_city")}</option>
        {cities.map((city, idx) => (
          <option key={idx} value={city}>
            {city}
          </option>
        ))}
      </select>
      <select
        value={filters.type}
        onChange={e => handleChange("type", e.target.value)}
        className="border rounded-lg px-3 py-2 w-[265px] focus:outline-none focus:ring-2 focus:ring-primary"
      >
        <option value="">{t("select_category")}</option>
        {categories.map(cat => (
          <option key={cat.id} value={cat.id}>
            {cat.languages[0]?.name || "No Name"}
          </option>
        ))}
      </select>
      <select
        value={filters.subType}
        onChange={e => handleChange("subType", e.target.value)}
        className="border rounded-lg px-3 py-2 w-[265px] focus:outline-none focus:ring-2 focus:ring-primary"
        disabled={!filters.type}
      >
        <option value="">{t("select_subtype")}</option>
        {subCategories.map(sub => (
          <option key={sub.id} value={sub.id}>
            {sub.languages[0]?.name || "No Name"}
          </option>
        ))}
      </select>

        <select
        value={filters.dateFilter}
        onChange={e => handleChange("dateFilter", e.target.value)}
        className="border rounded-lg px-3 py-2 w-[265px] focus:outline-none focus:ring-2 focus:ring-primary"
        >
        <option value="">{t("select_date_filter")}</option>
        {dateFilters.map(df => (
            <option key={df.id} value={df.id}>
            {df.name}
            </option>
        ))}
        </select>

      <button
        type="submit"
        className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/80 transition"
      >
        <Search />
      </button>
    </form>
  );
};

export default SearchForm;
